import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { Button } from "@tellescope/ui";
import { DndContext, type DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { ArrowLeft, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { VitalProgram, VitalPrograms } from "../useVitalsSettings";
import { useEditProgramsView } from "./useEditProgramsView";
import { SortableProgramRow } from "./SortableProgramRow";

export interface EditProgramsViewProps {
  initialPrograms: VitalPrograms;
  vitalConfigurations: TellescopeModelFromName<"vital_configurations">[];
  onSave: (programs: VitalProgram[]) => Promise<void>;
  onCancel: () => void;
}

export const EditProgramsView = ({ initialPrograms, vitalConfigurations, onSave, onCancel }: EditProgramsViewProps) => {
  const { t } = useTranslation("vitals");
  const {
    programs,
    addProgram,
    removeProgram,
    updateProgramTitle,
    updateProgramConfigIds,
    reorderPrograms,
    handleSave,
    configOptions,
  } = useEditProgramsView(initialPrograms, vitalConfigurations, onSave, onCancel);

  const sensors = useSensors(useSensor(PointerSensor));

  const sortableIds = programs.map((p) => p._id);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = sortableIds.indexOf(active.id as string);
      const newIndex = sortableIds.indexOf(over.id as string);
      reorderPrograms(arrayMove(programs, oldIndex, newIndex));
    }
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={onCancel}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-lg font-semibold">{t("Edit Programs")}</h2>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          <div className="flex flex-col gap-2">
            {programs.map((program) => (
              <SortableProgramRow
                key={program._id}
                programId={program._id}
                program={program}
                configOptions={configOptions}
                onTitleChange={updateProgramTitle}
                onConfigIdsChange={updateProgramConfigIds}
                onRemove={removeProgram}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <Button variant="outline" className="w-full border-dashed" onClick={addProgram}>
        <Plus className="h-4 w-4" />
        {t("Add Program Item")}
      </Button>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>
          {t("Cancel")}
        </Button>
        <Button onClick={handleSave}>{t("Save")}</Button>
      </div>
    </div>
  );
};
